import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { Chip, Tooltip, IconButton, Badge } from "@mui/material";
import { DeleteSharp, EditSharp, RemoveRedEye, Schedule } from "@mui/icons-material";
import moment from "moment";
import { Routes } from "src/constant/api_url";
import CustomLoader from "src/components/CustomLoader";
import ScheduleModal from "./ScheduleModal";

function NotificationTable({
  data,
  isLoading,
  loginUser,
  onDelete,
  onEdit,
  onViewRead,
  pagination,
  onPageChange,
  onPerRowsChange
}) {
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleParentRow, setScheduleParentRow] = useState(null);

  const isEditable = (row) =>
    row.notification_send_status !== 1 && row.notification_send_status !== "1";
  const handleOpenSchedule = (row) => {
    setScheduleParentRow(row);
    setScheduleModalOpen(true);
  };

  const columns = [
    { name: "No", wrap: true, selector: (row) => row.n_id, sortable: true, width: "70px" },
    { name: "Type", wrap: true, width: "80px", selector: (row) => row.Notification_type },
    {
      name: "Image/Audio/Video",
      wrap: true,
      width: "150px",
      cell: (row) => {
        if (row.n_type === 1)
          return (
            <audio
              controls
              src={`${Routes.API_HOSTNAME}images/notification/${row.n_image}`}
            />
          );
        if (row.n_type === 0)
          return (
            <img
              src={`${Routes.API_HOSTNAME}images/notification/${row.n_image}`}
              alt="img"
              width="100"
            />
          );
        if (row.n_type === 2) return row.n_video;
        return "-";
      }
    },
    {
      name: "Title",
      wrap: false,
      width: "150px",
      sortable: true,
      cell: (row) => {
        const c = row.n_title ?? "-";
        return (
          <div title={c}>{c.length > 30 ? c.slice(0, 30) + "..." : c}</div>
        );
      }
    },
    {
      name: "URL",
      wrap: true,
      width: "120px",
      selector: (row) => row.n_link || "-",
      sortable: true
    },
    {
      name: "Button Title",
      wrap: true,
      width: "120px",
      selector: (row) => row.n_button_title || "-",
      sortable: true
    },
    {
      name: "Question",
      wrap: false,
      width: "150px",
      sortable: true,
      cell: (row) => {
        const c = row.n_question ?? "-";
        return (
          <div title={c}>{c.length > 30 ? c.slice(0, 30) + "..." : c}</div>
        );
      }
    },
    {
      name: "Answer",
      wrap: false,
      width: "150px",
      sortable: true,
      cell: (row) => {
        const c = row.n_answer ?? "-";
        return (
          <div
            title={c}
            dangerouslySetInnerHTML={{
              __html: c.length > 30 ? c.slice(0, 30) + "..." : c
            }}
          />
        );
      }
    },
    {
      name: "Notification Send To",
      width: "90px",
      wrap: true,
      selector: (row) => row.Notification_Send_To
    },
    {
      name: "Notification For",
      width: "140px",
      wrap: true,
      selector: (row) => row.Notification_For
    },
    {
      name: "Notification To",
      width: "140px",
      wrap: true,
      selector: (row) => row.Notification_To
    },
    {
      name: "Location",
      wrap: true,
      width: "100px",
      selector: (row) =>
        loginUser.u_type != 0
          ? row.city_name
          : row.n_for_type == 1
            ? row.s_name
            : row.n_for_type == 2
              ? row.city_name
              : "LVB Prime"
    },
    {
      name: "Start Date",
      wrap: true,
      width: "120px",
      selector: (row) => row.s_date,
      sortable: true
    },
    {
      name: "End Date",
      selector: (row) => row.e_date,
      wrap: true,
      width: "120px",
      sortable: true
    },
    {
      name: "Created Date",
      selector: (row) => moment(row.timestamp).format("DD-MM-YYYY hh:mm A"),
      wrap: true,
      width: "180px",
      sortable: true
    },
    {
      name: "Schedules",
      width: "100px",
      center: true,
      ignoreRowClick: true,
      cell: (row) => {
        const count = row.sub_count || 0;
        if (count <= 0) return null;
        return (
          <Tooltip title={`${count} schedule ${count === 1 ? "entry" : "entries"}`}>
            <IconButton size="small" onClick={() => handleOpenSchedule(row)}>
              <Badge badgeContent={count} color="primary" max={99}>
                <Schedule fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        );
      }
    },
    {
      name: "Action",
      wrap: true,
      width: "150px",
      cell: (row) => {
        const editable = isEditable(row);
        return (
          <>
            <Tooltip title={!editable ? "Cannot delete sent notification" : "Delete"}>
              <span>
                <IconButton disabled={!editable} onClick={() => onDelete(row)}>
                  <DeleteSharp color={!editable ? "disabled" : "error"} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={!editable ? "Cannot edit sent notification" : "Edit"}>
              <span>
                <IconButton disabled={!editable} onClick={() => onEdit(row)}>
                  <EditSharp color={!editable ? "disabled" : "warning"} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="See Assign Member List">
              <IconButton onClick={() => onViewRead(row.n_id)}>
                <RemoveRedEye color="success" />
              </IconButton>
            </Tooltip>
          </>
        );
      }
    }
  ];

  return (
    <>
      <DataTable
        highlightOnHover
        columns={columns}
        data={data != null && data}
        progressPending={isLoading}
        progressComponent={<CustomLoader />}
        pagination
        paginationServer
        paginationTotalRows={pagination?.totalRecords || 0}
        paginationPerPage={pagination?.perPage || 10}
        paginationDefaultPage={pagination?.currentPage || 1}
        onChangePage={onPageChange}
        onChangeRowsPerPage={onPerRowsChange}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        parentRow={scheduleParentRow}
        onClose={() => {
          setScheduleModalOpen(false);
          setScheduleParentRow(null);
        }}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewRead={onViewRead}
      />
    </>
  );
}
export default NotificationTable;
